import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';

import { DeviceDetectorService } from 'ngx-device-detector';
import { MapService } from 'src/app/core/services/map.service';

import Radar from 'radar-sdk-js';
import RadarMap from 'radar-sdk-js/dist/ui/RadarMap';
import RadarMarker from 'radar-sdk-js/dist/ui/RadarMarker';

@Component({
  standalone: true,
  selector: 'app-hotels-map',
  templateUrl: './hotels-map.component.html',
  styleUrls: ['../maps.style.scss'],
})
export class HotelsMapComponent implements OnDestroy {
  @Input() needBottomPadding: boolean = false;
  @Output() selectedHotelIndex: EventEmitter<number> = new EventEmitter();

  private map: RadarMap;
  private mapMarkers: RadarMarker[] = [];

  @ViewChild('map', { static: true })
  private mapElement: ElementRef<HTMLElement>;

  readonly #mapService: MapService = inject(MapService);
  readonly #deviceDetectorService: DeviceDetectorService = inject(
    DeviceDetectorService,
  );

  @Input() set hotels(hotels: any[]) {
    if (hotels && hotels.length) {
      if (!this.map) {
        const firstHotel = hotels[0];
        this.initMap([firstHotel.longitude, firstHotel.latitude]);
      }

      if (this.mapMarkers.length) {
        this.mapMarkers.forEach((marker) => marker.remove());
      }

      if (this.#deviceDetectorService.userAgent) {
        setTimeout(() => {
          hotels.forEach((d) => {
            const marker: any | RadarMarker = Radar.ui.marker({
              anchor: 'bottom',
              color: '#499FDD',
              className: 'map-m-container',
              popup: {
                closeButton: false,
                className: 'map-b-container',
                element: this.#mapService.createBookingPopupElement(
                  d.providerURL || d.url,
                ),
              },
            });
            marker.setLngLat([d.longitude, d.latitude]).addTo(this.map);
            marker.on('click', () => this.selectedHotelIndex.emit(d.id));
            this.mapMarkers.push(marker);

            const paddingConfig = this.needBottomPadding
              ? { top: 50, bottom: 300, left: 35, right: 35 }
              : 50;
            this.map.fitToMarkers({ padding: paddingConfig });
          });
        }, 150);
      }
    }
  }

  readonly #ngZone: NgZone = inject(NgZone);

  ngOnDestroy(): void {
    const userAgent: string = this.#deviceDetectorService.userAgent;
    if (userAgent) {
      if (this.map) {
        this.map.remove();
        this.map = null;
      }
    }
  }

  private initMap(coordinates: [number, number]): void {
    const userAgent: string = this.#deviceDetectorService.userAgent;

    if (userAgent && !this.map) {
      const { nativeElement } = this.mapElement;
      this.#ngZone.runOutsideAngular(() => {
        this.map = Radar.ui.map({
          container: nativeElement,
          style: 'radar-default-v1',
          showZoomControls: false,
          center: coordinates,
          zoom: 5,
          trackResize: true,
        });
      });
    }
  }
}
